import { useEffect, useMemo, useRef, useState } from 'react'
import { Loader2, Lock } from 'lucide-react'
import { useGetUsersQuery } from '@axilon-apps/domains-identity/state'
import {
  useCreateResourceAccessRequestMutation,
  useLookupResourceAccessQuery,
  type ResourceAccessResourceType
} from '@axilon-apps/domains-ontology-ui-state'

import { Button } from '../ui/button'
import { Textarea } from '../ui/textarea'

function ownerLabel(user: { name: string; username: string } | undefined): string {
  if (!user) return 'the resource owner'
  if (user.name) return user.name
  return user.username || 'the resource owner'
}

function isNotFoundError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    (error as { status: unknown }).status === 404
  )
}

export interface RequestAccessPanelProps {
  resourceType: ResourceAccessResourceType
  path?: string
  id?: string
  onAccessGranted?: () => void
  onNotFound?: () => void
  notFoundMessage?: string
}

/**
 * Shown when a deep link addresses a resource the caller cannot view.
 * Lookup is the existence leak: a real 404 stays "not found"; a restricted
 * resource offers a request form targeted at the owner.
 */
export function RequestAccessPanel({
  resourceType,
  path,
  id,
  onAccessGranted,
  onNotFound,
  notFoundMessage = 'Resource not found.'
}: RequestAccessPanelProps) {
  const skip = resourceType === 'dashboard' ? !path : !id
  const lookup = useLookupResourceAccessQuery(
    {
      resourceType,
      ...(path ? { path } : {}),
      ...(id ? { id } : {})
    },
    { skip }
  )
  const users = useGetUsersQuery({ page: 1, perPage: 100 }, { skip: skip || !lookup.data })
  const [createRequest, createState] = useCreateResourceAccessRequestMutation()
  const [message, setMessage] = useState('')
  const [submitError, setSubmitError] = useState<string | null>(null)
  const notifiedAccess = useRef<string | null>(null)
  const notifiedMissing = useRef(false)

  const ownerName = useMemo(() => {
    const ownerId = lookup.data?.ownerUserId
    if (!ownerId) return 'the resource owner'
    const user = (users.data?.items ?? []).find((item) => item.id === ownerId)
    return ownerLabel(user)
  }, [lookup.data?.ownerUserId, users.data?.items])

  useEffect(() => {
    if (!lookup.data?.canAccess) return
    const key = lookup.data.resourceId
    if (notifiedAccess.current === key) return
    notifiedAccess.current = key
    onAccessGranted?.()
  }, [lookup.data, onAccessGranted])

  useEffect(() => {
    if (!isNotFoundError(lookup.error) || notifiedMissing.current) return
    notifiedMissing.current = true
    onNotFound?.()
  }, [lookup.error, onNotFound])

  const handleRequest = async () => {
    const scopeId = lookup.data?.scopeId
    if (!scopeId) return
    setSubmitError(null)
    try {
      await createRequest({ scopeId, message }).unwrap()
    } catch {
      setSubmitError('Couldn’t send the request. Please try again.')
    }
  }

  if (lookup.isLoading || (lookup.isFetching && !lookup.data)) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-12 text-center">
        <Loader2 className="size-5 animate-spin text-ink-3" aria-hidden />
        <p className="m-0 text-sm text-ink-3">Checking access…</p>
      </div>
    )
  }

  if (isNotFoundError(lookup.error)) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-12 text-center">
        <Lock className="size-8 text-ink-3" aria-hidden />
        <p className="m-0 text-sm text-ink-3">{notFoundMessage}</p>
      </div>
    )
  }

  if (lookup.error || !lookup.data) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-12 text-center">
        <p className="m-0 text-sm text-st-down">Couldn’t check access. Please try again.</p>
      </div>
    )
  }

  if (lookup.data.canAccess) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-12 text-center">
        <Loader2 className="size-5 animate-spin text-ink-3" aria-hidden />
        <p className="m-0 text-sm text-ink-3">You have access. Loading…</p>
      </div>
    )
  }

  const pending = lookup.data.pendingRequest
  const canRequest = lookup.data.canRequest && Boolean(lookup.data.scopeId)

  return (
    <div className="flex h-full flex-col items-center justify-center p-12">
      <div className="flex w-full max-w-md flex-col gap-4 text-center">
        <Lock className="mx-auto size-8 text-ink-3" aria-hidden />
        <div className="flex flex-col gap-1">
          <h1 className="m-0 text-lg font-semibold tracking-tight text-foreground">
            You don’t have access
          </h1>
          <p className="m-0 text-sm text-muted-foreground">
            {pending
              ? `Access requested. Waiting for ${ownerName} to respond.`
              : canRequest
                ? `Request access from ${ownerName}.`
                : 'This resource has no owner to request from.'}
          </p>
        </div>
        {pending ? (
          pending.message ? (
            <p className="m-0 rounded-md border border-border bg-muted/40 px-3 py-2 text-left text-sm text-muted-foreground">
              {pending.message}
            </p>
          ) : null
        ) : canRequest ? (
          <>
            <Textarea
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              placeholder="Add a note for the owner (optional)"
              rows={4}
              maxLength={2000}
              aria-label="Access request message"
            />
            {submitError ? <p className="m-0 text-sm text-st-down">{submitError}</p> : null}
            <Button
              type="button"
              onClick={() => void handleRequest()}
              disabled={createState.isLoading}
            >
              {createState.isLoading ? 'Sending…' : 'Request access'}
            </Button>
          </>
        ) : null}
      </div>
    </div>
  )
}
